<template>
  <!-- v-avatar ya recorta en círculo, así que el `img` de reserva no necesita
       ser un v-img: el `v-img` se usa solo para la foto remota, por su carga
       diferida y su evento de error. -->
  <v-avatar :size="size" color="grey lighten-3">
    <v-img v-if="src" :src="src" :alt="alt" @error="onError">
      <!-- Mientras carga, el avatar por defecto en vez de un hueco gris: al
           hacer scroll las filas entran de golpe y el parpadeo se nota más
           que la imagen. -->
      <template v-slot:placeholder>
        <img :src="fallback" :alt="alt" :width="size" :height="size" />
      </template>
    </v-img>
    <img v-else :src="fallback" :alt="alt" :width="size" :height="size" />
  </v-avatar>
</template>

<script>
/**
 * La foto de perfil de un usuario, con las mismas reglas que la app.
 *
 * LA REGLA VIENE DE LA APP, no se inventó aquí: `photoURL || photo`, y si no
 * hay ninguna, el avatar por defecto. Está así en los 14 lugares de
 * `maxpro_app/src` que pintan un avatar, sin una sola excepción al revés, y con
 * chequeo de falsy —una cadena vacía cuenta como "sin foto"—.
 *
 * DOS COSAS QUE CONVIENE SABER DE LOS DATOS:
 *
 *   · El índice de Algolia que alimenta la tabla de usuarios guarda `photo`
 *     pero NO `photoURL`. Aun así la expresión se deja completa, porque es la
 *     regla de la app y porque otras vistas leen de Firestore, donde sí están
 *     los dos. En Firestore `photo` es superconjunto de `photoURL` (201 contra
 *     173 en una muestra de 500), así que el `||` nunca se queda corto.
 *
 *   · Solo ~40% de los usuarios tienen foto. El avatar por defecto es el caso
 *     normal, no la excepción.
 *
 * DÓNDE SE APARTA DE LA APP, a propósito: si la URL existe pero falla al
 * cargar, aquí cae al avatar por defecto. La app reintenta dos veces y luego
 * deja el hueco vacío (ver `ProgressiveImg.component.tsx`), lo cual pasa
 * desapercibido en una pantalla pero en una tabla de cien filas deja círculos
 * vacíos salteados. Y pasa: hay usuarios con URL de `graph.facebook.com`, que
 * caducan. Se cumple la regla escrita —"la foto, si no el avatar por
 * defecto"—; una URL rota es una foto que no está.
 */
export default {
  name: "user-avatar",
  props: {
    /** El usuario, tal como llega de Algolia o de Firestore. */
    user: {
      type: Object,
      default: () => ({}),
    },
    size: {
      type: [Number, String],
      default: 40,
    },
  },
  data: () => ({
    // Se marca cuando la imagen remota no cargó, para no volver a intentarla.
    failed: false,
    fallback: require("@/assets/default-avatar.png"),
  }),
  computed: {
    src() {
      if (this.failed) return null;
      return this.user.photoURL || this.user.photo || null;
    },
    alt() {
      const nombre = [this.user.firstName, this.user.lastName]
        .filter(Boolean)
        .join(" ");
      return nombre || this.user.displayName || this.user.email || "User";
    },
  },
  watch: {
    /**
     * La tabla reutiliza los componentes entre páginas. Sin esto, una fila que
     * falló se quedaría con el avatar por defecto aunque el siguiente usuario
     * que caiga en esa posición sí tenga foto.
     */
    "user.photo": "reset",
    "user.photoURL": "reset",
  },
  methods: {
    reset() {
      this.failed = false;
    },
    onError() {
      this.failed = true;
    },
  },
};
</script>
